
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { S3File, DisplayedImage, EditMode, QuickEditAction, FilterType, ActiveFilter, NavigationState, AppView, UserTier, UserProfile, StarType, SaveDestinationType, CustomFolder } from './types';
import { fetchImageMetadata, saveGeneratedImage, exportFinalImage, saveImageStar } from './services/s3Service';
import { generateImageWithGemini, analyzeImageWithGemini } from './services/geminiService';
import ImageBrowser from './components/ImageBrowser';
import ImageViewer from './components/ImageViewer';
import EditPanel from './components/EditPanel';
import LoadingSpinner from './components/LoadingSpinner';
import LandingPage from './components/LandingPage';
import HomeIcon from './components/HomeIcon';
import ArrowLeftIcon from './components/ArrowLeftIcon';
import ArrowRightIcon from './components/ArrowRightIcon';
import StorePage from './components/StorePage';
import UserIcon from './components/UserIcon';
import UserProfilePage from './components/UserProfilePage';
import DebugViewIndicator from './components/DebugViewIndicator';
import CloseIcon from './components/CloseIcon'; 
import { MOCK_API_KEY_INFO, QUICK_EDIT_ACTIONS as DEFAULT_QUICK_EDIT_ACTIONS, GENERATED_IMAGES_FASE_PATH, EXPORTED_IMAGES_FASE_PATH, USER_CREATED_WORK_SPANISH_NAME, USER_EXPORTED_ART_SPANISH_NAME, FOLDER_COLORS, CUSTOM_FOLDERS_STORAGE_KEY } from './constants';

export const formatImageSuffix = (dateStr?: string, timeStr?: string, machineCode?: string): string | null => {
  const dateTimeParts: string[] = [];
  if (dateStr && timeStr) {
    try {
      const [year, month, day] = dateStr.split('-');
      const [hour, minute] = timeStr.split('-');
      if (year && month && day && hour && minute) {
        dateTimeParts.push(`${day}/${month}/${year.substring(2)} ${hour}:${minute}`);
      }
    } catch (e) { console.error("Error formatting date/time part for suffix:", e); }
  } else if (dateStr) {
     try {
      const [year, month, day] = dateStr.split('-');
      if (year && month && day) {
        dateTimeParts.push(`${day}/${month}/${year.substring(2)}`);
      }
    } catch (e) { console.error("Error formatting date part for suffix:", e); }
  } else if (timeStr) {
    try {
      const [hour, minute] = timeStr.split('-');
      if (hour && minute) {
        dateTimeParts.push(`${hour}:${minute}`);
      }
    } catch (e) { console.error("Error formatting time part for suffix:", e); }
  }

  if (machineCode) {
    dateTimeParts.push(machineCode);
  }

  if (dateTimeParts.length === 0) return null;
  return ` (${dateTimeParts.join(' - ')})`;
};

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<AppView>('landing');
  const [selectedS3File, setSelectedS3File] = useState<S3File | null>(null);
  const [currentDisplayedImage, setCurrentDisplayedImage] = useState<DisplayedImage | null>(null);
  const [imageForStore, setImageForStore] = useState<DisplayedImage | null>(null);

  const [isLoadingDescription, setIsLoadingDescription] = useState<boolean>(false);
  const [isLoadingOperation, setIsLoadingOperation] = useState<boolean>(false);
  const [isAutoSavingImage, setIsAutoSavingImage] = useState<boolean>(false); 
  const [isSavingToDrafts, setIsSavingToDrafts] = useState<boolean>(false); 
  const [isSavingToFinalArtwork, setIsSavingToFinalArtwork] = useState<boolean>(false); 
  const [isSavingToCustomFolderId, setIsSavingToCustomFolderId] = useState<string | null>(null);
  const [showSaveOptionsButton, setShowSaveOptionsButton] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);
  const [autoSaveMessage, setAutoSaveMessage] = useState<string | null>(null); 
  const [userSaveMessage, setUserSaveMessage] = useState<string | null>(null); 

  // States for modal and pending notification
  const [isSaveModalCurrentlyOpen, setIsSaveModalCurrentlyOpen] = useState<boolean>(false);
  const [pendingNotificationMessage, setPendingNotificationMessage] = useState<{ type: 'user' | 'auto' | 'error', message: string } | null>(null);


  const [editMode, setEditMode] = useState<EditMode>(EditMode.Generate);
  const [generationPrompt, setGenerationPrompt] = useState<string>("");
  const [analysisPrompt, setAnalysisPrompt] = useState<string>("Describe esta imagen en detalle.");
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);

  const [customQuickEditActions, setCustomQuickEditActions] = useState<QuickEditAction[]>([]);
  const [customFolders, setCustomFolders] = useState<CustomFolder[]>([]);

  const [browseToPath, setBrowseToPath] = useState<string>("");
  const [isUserGalleryActive, setIsUserGalleryActive] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter | null>(null);
  const [refreshCounter, setRefreshCounter] = useState<number>(0);

  const [navigationHistory, setNavigationHistory] = useState<NavigationState[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState<number>(-1);

  const [currentUserTier, setCurrentUserTier] = useState<UserTier>(UserTier.Free);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    username: "PPLopez69",
    email: "pplopez69@example.com",
    registrationDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(), 
    tier: UserTier.Free,
  });

  const [leftPanelWidthPercent, setLeftPanelWidthPercent] = useState<number>(25); 
  const [topRightPanelHeightPercent, setTopRightPanelHeightPercent] = useState<number>(60); 
  const [isResizingHorizontal, setIsResizingHorizontal] = useState<boolean>(false);
  const [isResizingVertical, setIsResizingVertical] = useState<boolean>(false);

  const horizontalResizeContainerRef = useRef<HTMLDivElement>(null);
  const verticalResizeContainerRef = useRef<HTMLDivElement>(null); 

  const [hoveredDebugElement, setHoveredDebugElement] = useState<string | null>(null);

  const [notificationBannerCountdown, setNotificationBannerCountdown] = useState<number | null>(null);
  const [notificationBannerAnimateXClick, setNotificationBannerAnimateXClick] = useState<boolean>(false);
  // Fix: Namespace 'global.NodeJS' has no exported member 'Timeout'. Using ReturnType<typeof setInterval> for cross-environment compatibility.
  const notificationBannerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setUserProfile(prev => ({ ...prev, tier: currentUserTier }));
  }, [currentUserTier]);

  useEffect(() => {
    const storedFolders = localStorage.getItem(CUSTOM_FOLDERS_STORAGE_KEY);
    if (storedFolders) {
      try {
        const parsedFolders = JSON.parse(storedFolders);
        if (Array.isArray(parsedFolders)) {
          setCustomFolders(parsedFolders);
        }
      } catch (e) {
        console.error("Error parsing custom folders from local storage:", e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CUSTOM_FOLDERS_STORAGE_KEY, JSON.stringify(customFolders));
  }, [customFolders]);

  const navigateAndRecordState = useCallback((
    newBrowseToPath: string,
    newIsUserGalleryActive: boolean,
    newActiveFilter: ActiveFilter | null,
    newSelectedS3File: S3File | null
  ) => {
    const nextState: NavigationState = {
      browseToPath: newBrowseToPath,
      isUserGalleryActive: newIsUserGalleryActive,
      activeFilter: newActiveFilter,
      selectedS3FileId: newSelectedS3File?.id || null,
    };

    const newHistory = navigationHistory.slice(0, currentHistoryIndex + 1);
    newHistory.push(nextState);
    setNavigationHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);

    setBrowseToPath(newBrowseToPath);
    setIsUserGalleryActive(newIsUserGalleryActive);
    setActiveFilter(newActiveFilter);

    if (newSelectedS3File?.id !== selectedS3File?.id) {
        setSelectedS3File(newSelectedS3File);
        if(!newSelectedS3File) {
            setCurrentDisplayedImage(null);
            setAnalysisResult(null);
            setShowSaveOptionsButton(false);
        }
    } else if (!newSelectedS3File && selectedS3File) {
        setSelectedS3File(null);
        setCurrentDisplayedImage(null);
        setAnalysisResult(null);
        setShowSaveOptionsButton(false);
    }
    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);

  }, [navigationHistory, currentHistoryIndex, selectedS3File?.id]);

   useEffect(() => {
    if (!process.env.API_KEY) {
        console.warn("La clave API de Gemini (variable de entorno API_KEY) no está configurada. Las funciones de Gemini podrían no funcionar.");
    }
    if (process.env.REACT_APP_WORDPRESS_API_URL) {
      console.info(`Intentando usar la API de WordPress en: ${process.env.REACT_APP_WORDPRESS_API_URL}`);
    } else {
      console.info("REACT_APP_WORDPRESS_API_URL no configurada. Usando datos simulados para la navegación y guardado S3.");
    }
  }, []);

  useEffect(() => {
    if (currentView === 'studio') {
        if (!selectedS3File && !isUserGalleryActive && !activeFilter && !currentDisplayedImage) { 
            setEditMode(EditMode.Generate);
            setShowSaveOptionsButton(false); 
        } else if (selectedS3File || currentDisplayedImage || activeFilter) { 
             setEditMode(EditMode.QuickEdit);
        }
    }
  }, [selectedS3File, currentDisplayedImage, isUserGalleryActive, currentView, activeFilter]);

  const handleSelectS3File = useCallback(async (s3File: S3File) => {
    navigateAndRecordState(browseToPath, isUserGalleryActive, activeFilter, s3File);

    setCurrentDisplayedImage(null);
    setAnalysisResult(null);
    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage(null);
    setIsLoadingDescription(true);
    setShowSaveOptionsButton(false); 
    try {
      const metadata = await fetchImageMetadata(s3File);
      const rawDescription = metadata.description;
      const star = metadata.star;

      setSelectedS3File(prev => prev && prev.id === s3File.id ? { ...prev, descriptionText: rawDescription } : s3File);

      let shortDescriptionForDisplay = rawDescription?.split('\n')[0] || s3File.displayName || s3File.name;
      if (shortDescriptionForDisplay.length > 100) shortDescriptionForDisplay = shortDescriptionForDisplay.substring(0,97) + "...";
      
      const isSavedToDrafts = s3File.prefix.startsWith(GENERATED_IMAGES_FASE_PATH);
      const isExportedToFinal = s3File.prefix.startsWith(EXPORTED_IMAGES_FASE_PATH);
      let savedToCustomFolderPathValue: string | undefined = undefined;
      if (isSavedToDrafts && !isExportedToFinal) {
          const pathParts = s3File.prefix.split('/').filter(Boolean);
          if (pathParts.length === 4 && pathParts[0] === GENERATED_IMAGES_FASE_PATH && pathParts[1] === userProfile.username) {
              const potentialCustomFolderName = pathParts[2];
              if (customFolders.some(cf => cf.name === potentialCustomFolderName)) {
                savedToCustomFolderPathValue = `${GENERATED_IMAGES_FASE_PATH}/${userProfile.username}/${potentialCustomFolderName}/`;
              }
          }
      }

      setCurrentDisplayedImage({
        id: s3File.id,
        url: s3File.url,
        alt: s3File.name, 
        displayName: s3File.displayName || s3File.name, 
        s3Prefix: s3File.prefix, 
        parsedDate: s3File.parsedDate,
        parsedTime: s3File.parsedTime,
        parsedMachineCode: s3File.parsedMachineCode,
        description: shortDescriptionForDisplay,
        rawDescription: rawDescription,
        isGenerated: isSavedToDrafts || isExportedToFinal,
        isSaved: isSavedToDrafts,
        isExported: isExportedToFinal,
        savedToCustomFolderPath: savedToCustomFolderPathValue,
        star: star,
      });
      setShowSaveOptionsButton(true); 

      const basePromptSubject = rawDescription || s3File.displayName || s3File.name;
      setGenerationPrompt(`Una obra de arte creativa inspirada en "${basePromptSubject.substring(0,100)}". Estilo: pintura digital.`);
      setAnalysisPrompt(`¿Cuáles son los elementos clave en la imagen titulada "${s3File.displayName || s3File.name}" según su contenido y descripción?`);
      setEditMode(EditMode.QuickEdit);
    } catch (err) {
      console.error("Error al cargar metadatos de la imagen:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setPendingNotificationMessage({type: 'error', message: `Error al cargar metadatos para ${s3File.displayName || s3File.name}. ${errorMessage}`});
      setCurrentDisplayedImage({ 
        id: s3File.id,
        url: s3File.url,
        alt: s3File.name,
        displayName: s3File.displayName || s3File.name,
        s3Prefix: s3File.prefix,
        parsedDate: s3File.parsedDate,
        parsedTime: s3File.parsedTime,
        parsedMachineCode: s3File.parsedMachineCode,
        isGenerated: false,
        star: StarType.None,
      });
      setShowSaveOptionsButton(true); 
      setEditMode(EditMode.QuickEdit);
    } finally {
      setIsLoadingDescription(false);
    }
  }, [browseToPath, isUserGalleryActive, activeFilter, navigateAndRecordState, userProfile.username, customFolders]);

  const baseGenerateImage = useCallback(async (prompt: string, appliedEditLabel?: string) => {
    if (!prompt) {
      setPendingNotificationMessage({type: 'error', message: "El prompt no puede estar vacío para la generación de imágenes."});
      return;
    }
    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage(null);
    setIsLoadingOperation(true);
    setAnalysisResult(null);
    setShowSaveOptionsButton(false); 

    let generatedBase64Image: string | null = null;
    const originalDisplayName = selectedS3File?.displayName || selectedS3File?.name || "Imagen original";

    try {
      generatedBase64Image = await generateImageWithGemini(prompt);
      const tempImage: DisplayedImage = {
        id: `generated-${Date.now()}`,
        url: `data:image/jpeg;base64,${generatedBase64Image}`,
        alt: appliedEditLabel ? `Vista previa: "${originalDisplayName}" (${appliedEditLabel})` : `Vista previa: ${prompt.substring(0,30)}...`,
        displayName: appliedEditLabel ? `Edit: ${appliedEditLabel} (${originalDisplayName.substring(0,15)}...)` : `Generada: ${prompt.substring(0,20)}...`,
        rawDescription: selectedS3File?.descriptionText, 
        isGenerated: true,
        promptUsed: prompt,
        appliedEdit: appliedEditLabel,
        isSaved: false, 
        isExported: false,
        star: StarType.None,
        s3Prefix: undefined, 
      };
      setCurrentDisplayedImage(tempImage);
      setSelectedS3File(null); 
      setShowSaveOptionsButton(true); 

      setIsAutoSavingImage(true);
      setPendingNotificationMessage({type: 'auto', message: "Autoguardando imagen..."});

      const pureBase64 = generatedBase64Image;
      const savedFile = await saveGeneratedImage(pureBase64, selectedS3File, prompt, appliedEditLabel, userProfile.username, undefined);

      if (savedFile) {
        const suffix = formatImageSuffix(savedFile.parsedDate, savedFile.parsedTime, savedFile.parsedMachineCode) || "";
        setCurrentDisplayedImage({
            id: savedFile.id,
            url: savedFile.url,
            alt: savedFile.name, 
            displayName: savedFile.displayName || savedFile.name, 
            s3Prefix: savedFile.prefix, 
            parsedDate: savedFile.parsedDate,
            parsedTime: savedFile.parsedTime,
            parsedMachineCode: savedFile.parsedMachineCode,
            rawDescription: savedFile.descriptionText,
            isGenerated: true,
            promptUsed: prompt,
            appliedEdit: appliedEditLabel,
            isSaved: true, 
            isExported: false,
            star: StarType.None, 
        });
        setPendingNotificationMessage({type: 'auto', message: `¡Imagen autoguardada como "${savedFile.displayName || savedFile.name}${suffix}" en '${USER_CREATED_WORK_SPANISH_NAME}'!`});
        setRefreshCounter(prev => prev + 1);
      } else {
        setPendingNotificationMessage({type: 'auto', message: "Error al autoguardar la imagen. Solo está disponible en vista previa."});
      }

    } catch (err) {
      console.error("Operación fallida (generación o autoguardado):", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      const userFriendlyError = errorMessage.includes("API Key") ? MOCK_API_KEY_INFO : errorMessage;
      setPendingNotificationMessage({type: 'error', message: `Operación fallida: ${userFriendlyError}`});
      if (generatedBase64Image && currentDisplayedImage && !currentDisplayedImage.isSaved) {
         setCurrentDisplayedImage(prev => prev ? {...prev, isSaved: false } : null);
         setShowSaveOptionsButton(true); 
      }
    } finally {
      setIsLoadingOperation(false);
      setIsAutoSavingImage(false);
    }
  }, [selectedS3File, currentDisplayedImage, userProfile.username]);

  const handleGenerateImageFromPrompt = useCallback(async () => {
    baseGenerateImage(generationPrompt);
  }, [generationPrompt, baseGenerateImage]);

  const handleApplyQuickEdit = useCallback(async (editAction: QuickEditAction) => {
    if (!selectedS3File && !currentDisplayedImage) { 
      setPendingNotificationMessage({type: 'error', message: "Por favor, selecciona o genera una imagen antes de aplicar una edición rápida."});
      return;
    }
    const baseSubjectSource = currentDisplayedImage?.rawDescription || currentDisplayedImage?.displayName || currentDisplayedImage?.alt || selectedS3File?.descriptionText || selectedS3File?.displayName || selectedS3File?.name;
    if (!baseSubjectSource) {
        setPendingNotificationMessage({type: 'error', message: "No se pudo determinar el sujeto base para la edición rápida."});
        return;
    }
    const newPrompt = editAction.promptGenerator(baseSubjectSource);
    baseGenerateImage(newPrompt, editAction.label);
  }, [selectedS3File, currentDisplayedImage, baseGenerateImage]);

  const handleAnalyzeImage = useCallback(async () => {
    if (!currentDisplayedImage || !currentDisplayedImage.url) {
      setPendingNotificationMessage({type: 'error', message: "Por favor, selecciona o genera una imagen para analizar."});
      return;
    }
    if (!analysisPrompt) {
      setPendingNotificationMessage({type: 'error', message: "Por favor, introduce un prompt para el análisis de imagen."});
      return;
    }
    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage(null);
    setIsLoadingOperation(true);
    setAnalysisResult(null);

    try {
      const contextForAnalysis = currentDisplayedImage.rawDescription ?
        `${analysisPrompt}\n\nContexto de la descripción original de la imagen:\n${currentDisplayedImage.rawDescription}`
        : analysisPrompt;

      const result = await analyzeImageWithGemini(contextForAnalysis, currentDisplayedImage.url, currentDisplayedImage.url.startsWith("data:image/png") ? "image/png" : "image/jpeg");
      setAnalysisResult(result);
      if(currentDisplayedImage) {
        setCurrentDisplayedImage({...currentDisplayedImage, analysisResult: result});
      }
    } catch (err) {
      console.error("Error al analizar la imagen:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setPendingNotificationMessage({type: 'error', message: `Análisis de imagen fallido: ${errorMessage.includes("API Key") ? MOCK_API_KEY_INFO : errorMessage}`});
    } finally {
      setIsLoadingOperation(false);
    }
  }, [currentDisplayedImage, analysisPrompt]);

  const getImageDataForSaving = async (image: DisplayedImage): Promise<string> => {
    if (image.url.startsWith('data:image')) {
      return image.url.split(',')[1];
    }
    const response = await fetch(image.url);
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to fetch image for saving. Status: ${response.status}. URL: ${image.url}. Response: ${errorText}`);
      throw new Error(`No se pudo obtener la imagen para guardar: ${response.statusText}`);
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = (error) => reject(new Error(`Error al leer la imagen: ${error}`));
      reader.readAsDataURL(blob);
    });
  };
  
  const handleSaveToDrafts = useCallback(async () => {
    if (!currentDisplayedImage) {
      setPendingNotificationMessage({ type: 'error', message: "No hay imagen para guardar en borradores." });
      return;
    }
    if (currentDisplayedImage.isSaved && !currentDisplayedImage.isExported && !currentDisplayedImage.savedToCustomFolderPath) {
      setPendingNotificationMessage({ type: 'user', message: "Esta imagen ya está guardada en borradores generales." });
      setShowSaveOptionsButton(false); 
      return;
    }
    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage({ type: 'user', message: "Guardando en borradores..." });
    setIsSavingToDrafts(true);
  
    try {
      const imageDataBase64 = await getImageDataForSaving(currentDisplayedImage);
      const originalS3Context = selectedS3File && selectedS3File.id === currentDisplayedImage.id ? selectedS3File : undefined;
      
      const savedFile = await saveGeneratedImage(
        imageDataBase64,
        originalS3Context,
        currentDisplayedImage.promptUsed,
        currentDisplayedImage.appliedEdit,
        userProfile.username,
        undefined 
      );
  
      if (savedFile) {
        const suffix = formatImageSuffix(savedFile.parsedDate, savedFile.parsedTime, savedFile.parsedMachineCode) || "";
        setCurrentDisplayedImage({
          ...currentDisplayedImage,
          id: savedFile.id,
          url: savedFile.url,
          alt: savedFile.name,
          displayName: savedFile.displayName || savedFile.name,
          s3Prefix: savedFile.prefix, 
          parsedDate: savedFile.parsedDate,
          parsedTime: savedFile.parsedTime,
          parsedMachineCode: savedFile.parsedMachineCode,
          rawDescription: savedFile.descriptionText,
          isGenerated: true, 
          isSaved: true,     
          isExported: false,
          savedToCustomFolderPath: undefined, 
        });
        setPendingNotificationMessage({ type: 'user', message: `¡Imagen guardada como "${savedFile.displayName || savedFile.name}${suffix}" en '${USER_CREATED_WORK_SPANISH_NAME}'!` });
        setRefreshCounter(prev => prev + 1);
        if (originalS3Context) {
            setSelectedS3File(null); 
        }
        setShowSaveOptionsButton(false); 
      } else {
        setPendingNotificationMessage({ type: 'user', message: "Error al guardar la imagen en borradores." });
        setShowSaveOptionsButton(true); 
      }
    } catch (err) {
      console.error("Error al guardar en borradores:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setPendingNotificationMessage({ type: 'error', message: `Error al guardar en borradores: ${errorMessage}` });
      setShowSaveOptionsButton(true); 
    } finally {
      setIsSavingToDrafts(false);
    }
  }, [currentDisplayedImage, selectedS3File, userProfile.username]);
  
  const handleSaveToFinalArtwork = useCallback(async () => {
    if (!currentDisplayedImage) {
      setPendingNotificationMessage({ type: 'error', message: "No hay imagen válida seleccionada o generada para grabar como final." });
      return;
    }
    if (currentDisplayedImage.isExported) {
        setPendingNotificationMessage({ type: 'user', message: "Esta imagen ya ha sido grabada como arte final." });
        setShowSaveOptionsButton(false); 
        return;
    }

    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage({ type: 'user', message: "Grabando como arte final..." });
    setIsSavingToFinalArtwork(true);
  
    try {
      const imageDataBase64 = await getImageDataForSaving(currentDisplayedImage);
      const contextFile: S3File = (selectedS3File && selectedS3File.id === currentDisplayedImage.id) ? selectedS3File : {
          itemType: 'file',
          id: currentDisplayedImage.id, 
          name: currentDisplayedImage.alt, 
          displayName: currentDisplayedImage.displayName,
          url: currentDisplayedImage.url, 
          prefix: currentDisplayedImage.s3Prefix || (currentDisplayedImage.isGenerated ? (currentDisplayedImage.isExported ? EXPORTED_IMAGES_FASE_PATH : GENERATED_IMAGES_FASE_PATH) + "/" + userProfile.username + "/" + (currentDisplayedImage.parsedDate || "unknown-date") + "/" : "unknown/"),
          descriptionTextPath: selectedS3File?.descriptionTextPath || (currentDisplayedImage.isGenerated ? (currentDisplayedImage.isExported ? EXPORTED_IMAGES_FASE_PATH : GENERATED_IMAGES_FASE_PATH) + "/" + userProfile.username + "/" + (currentDisplayedImage.parsedDate || "unknown-date") + "/" + currentDisplayedImage.alt.replace(/\.[^/.]+$/, "") + ".txt" : "unknown/" + currentDisplayedImage.alt.replace(/\.[^/.]+$/, "") + ".txt"),
          descriptionText: currentDisplayedImage.rawDescription,
          parsedDate: currentDisplayedImage.parsedDate, 
          parsedTime: currentDisplayedImage.parsedTime, 
          parsedMachineCode: currentDisplayedImage.parsedMachineCode,
          star: currentDisplayedImage.star || StarType.None,
      };
  
      // Fix: 'currentAppliedEditLabel' was undefined. Use 'currentDisplayedImage.appliedEdit' instead.
      const exportedFile = await exportFinalImage(
        contextFile,
        imageDataBase64,
        userProfile.username,
        currentDisplayedImage.promptUsed,
        currentDisplayedImage.appliedEdit
      );
  
      if (exportedFile) {
        const suffix = formatImageSuffix(exportedFile.parsedDate, exportedFile.parsedTime, exportedFile.parsedMachineCode) || "";
        setPendingNotificationMessage({ type: 'user', message: `¡Imagen grabada como "${exportedFile.displayName || exportedFile.name}${suffix}" a '${USER_EXPORTED_ART_SPANISH_NAME}'!` });
        setCurrentDisplayedImage(prev => prev ? { 
            ...prev, 
            id: exportedFile.id, 
            url: exportedFile.url, 
            alt: exportedFile.name,
            displayName: exportedFile.displayName || exportedFile.name,
            s3Prefix: exportedFile.prefix, 
            parsedDate: exportedFile.parsedDate,
            parsedTime: exportedFile.parsedTime,
            parsedMachineCode: exportedFile.parsedMachineCode,
            rawDescription: exportedFile.descriptionText,
            isSaved: true, 
            isExported: true, 
            savedToCustomFolderPath: undefined, 
            star: prev.isExported ? prev.star : StarType.None, 
        } : null); 
        setRefreshCounter(prev => prev + 1);
        setSelectedS3File(null);
        setShowSaveOptionsButton(false); 
      } else {
        setPendingNotificationMessage({ type: 'user', message: "Error al grabar la imagen como final." });
        setShowSaveOptionsButton(true); 
      }
    } catch (err) {
      console.error("Error al grabar como final:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setPendingNotificationMessage({ type: 'error', message: `Error al grabar como final: ${errorMessage}` });
      setShowSaveOptionsButton(true); 
    } finally {
      setIsSavingToFinalArtwork(false);
    }
  }, [currentDisplayedImage, selectedS3File, userProfile.username]);

  const handleSaveToCustomFolder = useCallback(async (customFolder: CustomFolder) => {
    if (!currentDisplayedImage) {
      setPendingNotificationMessage({ type: 'error', message: "No hay imagen para guardar." });
      return;
    }
    const targetS3Prefix = `${GENERATED_IMAGES_FASE_PATH}/${userProfile.username}/${customFolder.name}/${currentDisplayedImage.parsedDate || new Date().toISOString().split('T')[0]}/`;
    if (currentDisplayedImage.s3Prefix === targetS3Prefix) {
        setPendingNotificationMessage({ type: 'user', message: `Esta imagen ya está guardada en la carpeta "${customFolder.name}" (fecha actual).` });
        setShowSaveOptionsButton(false); 
        return;
    }

    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage({ type: 'user', message: `Guardando en "${customFolder.name}"...` });
    setIsSavingToCustomFolderId(customFolder.id);

    try {
        const imageDataBase64 = await getImageDataForSaving(currentDisplayedImage);
        const originalS3Context = selectedS3File && selectedS3File.id === currentDisplayedImage.id ? selectedS3File : undefined;

        const savedFile = await saveGeneratedImage(
            imageDataBase64,
            originalS3Context,
            currentDisplayedImage.promptUsed,
            currentDisplayedImage.appliedEdit,
            userProfile.username,
            customFolder.name 
        );

        if (savedFile) {
            const suffix = formatImageSuffix(savedFile.parsedDate, savedFile.parsedTime, savedFile.parsedMachineCode) || "";
            const customFolderPathForDisplay = `${GENERATED_IMAGES_FASE_PATH}/${userProfile.username}/${customFolder.name}/`; 
            
            setCurrentDisplayedImage({
                ...currentDisplayedImage,
                id: savedFile.id,
                url: savedFile.url,
                alt: savedFile.name,
                displayName: savedFile.displayName || savedFile.name,
                s3Prefix: savedFile.prefix, 
                parsedDate: savedFile.parsedDate,
                parsedTime: savedFile.parsedTime,
                parsedMachineCode: savedFile.parsedMachineCode,
                rawDescription: savedFile.descriptionText,
                isGenerated: true,
                isSaved: true,
                isExported: false,
                savedToCustomFolderPath: customFolderPathForDisplay, 
            });
            setPendingNotificationMessage({ type: 'user', message: `¡Imagen guardada como "${savedFile.displayName || savedFile.name}${suffix}" en la carpeta "${customFolder.name}"!` });
            setRefreshCounter(prev => prev + 1);
            if (originalS3Context) {
                setSelectedS3File(null);
            }
            setShowSaveOptionsButton(false); 
        } else {
            setPendingNotificationMessage({ type: 'user', message: `Error al guardar la imagen en "${customFolder.name}".` });
            setShowSaveOptionsButton(true); 
        }
    } catch (err) {
        console.error(`Error al guardar en carpeta "${customFolder.name}":`, err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        setPendingNotificationMessage({ type: 'error', message: `Error al guardar en "${customFolder.name}": ${errorMessage}` });
        setShowSaveOptionsButton(true); 
    } finally {
        setIsSavingToCustomFolderId(null);
    }
}, [currentDisplayedImage, selectedS3File, userProfile.username]);

  useEffect(() => {
    if (!isSaveModalCurrentlyOpen && pendingNotificationMessage) {
        // Small delay to ensure modal state is fully processed if needed.
        // Can be removed if direct state update works consistently.
        const timer = setTimeout(() => {
            if (pendingNotificationMessage.type === 'user') {
                setUserSaveMessage(pendingNotificationMessage.message);
                setError(null);
                setAutoSaveMessage(null);
            } else if (pendingNotificationMessage.type === 'auto') {
                setAutoSaveMessage(pendingNotificationMessage.message);
                setError(null);
                setUserSaveMessage(null);
            } else if (pendingNotificationMessage.type === 'error') {
                setError(pendingNotificationMessage.message);
                setUserSaveMessage(null);
                setAutoSaveMessage(null);
            }
            setPendingNotificationMessage(null);
        }, 50); // 50ms delay
        return () => clearTimeout(timer);
    }
  }, [isSaveModalCurrentlyOpen, pendingNotificationMessage]);


  const handleAddCustomQuickEdit = useCallback((action: Omit<QuickEditAction, 'id' | 'promptGenerator' | 'isCustom'> & { promptTemplate: string }) => {
    const newAction: QuickEditAction = {
      ...action,
      id: `custom-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      promptGenerator: (baseSubject: string) => action.promptTemplate.replace(/\{baseSubject\}/g, baseSubject),
      isCustom: true,
    };
    setCustomQuickEditActions(prevActions => [...prevActions, newAction]);
  }, []);

  const handleAddCustomFolder = useCallback(async (folderName: string): Promise<boolean> => {
    const trimmedName = folderName.trim();
    if (!trimmedName) {
      return false;
    }
    if (customFolders.some(f => f.name.toLowerCase() === trimmedName.toLowerCase())) {
      return false;
    }

    const newFolder: CustomFolder = {
      id: `custom-folder-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
      name: trimmedName,
      color: FOLDER_COLORS[customFolders.length % FOLDER_COLORS.length],
    };
    setCustomFolders(prev => [...prev, newFolder]);
    return true;
  }, [customFolders]);


  const handleHeaderUserClick = () => {
    if (currentView === 'studio' && isUserGalleryActive && browseToPath !== "") {
        setBrowseToPath("");
        setIsUserGalleryActive(true);
        setActiveFilter(null);
        setSelectedS3File(null);
        const nextState: NavigationState = {
            browseToPath: "",
            isUserGalleryActive: true,
            activeFilter: null,
            selectedS3FileId: null,
        };
        const newHistory = navigationHistory.slice(0, currentHistoryIndex + 1);
        newHistory.push(nextState);
        setNavigationHistory(newHistory);
        setCurrentHistoryIndex(newHistory.length - 1);
        setEditMode(EditMode.QuickEdit);
        setRefreshCounter(prev => prev + 1); 
        setError(null);
        setAutoSaveMessage(null);
        setUserSaveMessage(null);
        setPendingNotificationMessage(null);
        setShowSaveOptionsButton(false); 
    } else {
        setCurrentView('profile');
    }
  };

  const handleTierChange = (newTier: UserTier) => {
    setCurrentUserTier(newTier);
  };

  const handleNavigateToUserGallery = () => {
    setCurrentView('studio');
    navigateAndRecordState(GENERATED_IMAGES_FASE_PATH + "/" + userProfile.username + "/", true, null, null);
    setEditMode(EditMode.QuickEdit);
  };

  const handleStudioTitleClick = () => {
    setCurrentView('studio');
    navigateAndRecordState("", false, null, null);
    setEditMode(EditMode.Generate);
    setShowSaveOptionsButton(false); 
  };

  const handleHomeIconClick = () => {
    setCurrentView('landing');
    setNavigationHistory([]);
    setCurrentHistoryIndex(-1);
    setBrowseToPath("");
    setIsUserGalleryActive(false);
    setActiveFilter(null);
    setSelectedS3File(null);
    setCurrentDisplayedImage(null);
    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage(null);
    setShowSaveOptionsButton(false);
  };

  const navigateToStudioSection = useCallback((
    targetMode: EditMode,
    targetIsUserGallery?: boolean,
    targetGalleryPath?: string
  ) => {
    setCurrentView('studio');
    setEditMode(targetMode);

    const path = targetGalleryPath === undefined ? "" : targetGalleryPath;
    const isGallery = targetIsUserGallery === undefined ? false : targetIsUserGallery;

    const initialState: NavigationState = {
        browseToPath: path,
        isUserGalleryActive: isGallery,
        activeFilter: null,
        selectedS3FileId: null,
    };
    setNavigationHistory([initialState]);
    setCurrentHistoryIndex(0);

    setBrowseToPath(path);
    setIsUserGalleryActive(isGallery);
    setActiveFilter(null);
    setSelectedS3File(null);
    setCurrentDisplayedImage(null);
    setAnalysisResult(null);
    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage(null);
    setShowSaveOptionsButton(false); 

    setRefreshCounter(prev => prev + 1);
  }, []);

  const handleNavigateToVideoFromImage = () => {
    alert("Próximamente: Funcionalidad para generar video desde una imagen.");
  };

  const handleNavigateToVideoWithAudio = () => {
    alert("Próximamente: Funcionalidad para generar video con audio.");
  };

  const handleNavigateToStore = () => {
    if (currentDisplayedImage) {
      setImageForStore(currentDisplayedImage);
      setCurrentView('store');
    } else {
      alert("Por favor, selecciona una imagen para verla en la tienda.");
    }
  };

  const handleBackToStudioFromProfile = () => {
    setCurrentView('studio');
  };

  const handleBackToStudioFromStore = () => {
    setCurrentView('studio');
  };

  const handleSetImageStar = useCallback(async (starType: StarType) => {
    if (!currentDisplayedImage) return;

    const oldStar = currentDisplayedImage.star;
    setCurrentDisplayedImage(prev => prev ? { ...prev, star: starType } : null);
    setPendingNotificationMessage({type: 'user', message: `Guardando marca "${starType === StarType.None ? 'ninguna' : starType.toLowerCase().replace('_',' ')}"...`});

    try {
      const success = await saveImageStar(currentDisplayedImage.id, starType);
      if (success) {
        setPendingNotificationMessage({type: 'user', message: `Marca "${starType === StarType.None ? 'ninguna' : starType.toLowerCase().replace('_',' ')}" guardada para ${currentDisplayedImage.displayName || currentDisplayedImage.alt}.`});
      } else {
        setCurrentDisplayedImage(prev => prev ? { ...prev, star: oldStar } : null); 
        setPendingNotificationMessage({type: 'error', message: `Error al guardar la marca para ${currentDisplayedImage.displayName || currentDisplayedImage.alt}.`});
      }
    } catch (err) {
      console.error("Error al guardar la marca:", err);
      setCurrentDisplayedImage(prev => prev ? { ...prev, star: oldStar } : null); 
      setPendingNotificationMessage({type: 'error', message: `Error al guardar la marca: ${err instanceof Error ? err.message : String(err)}`});
    }
  }, [currentDisplayedImage]);

  const handleApplyImageViewerFilter = useCallback((type: FilterType, value: string, displayName: string) => {
    setCurrentView('studio');
    navigateAndRecordState("filter_view", false, { type, value, displayName }, null);
    setEditMode(EditMode.QuickEdit);
  }, [navigateAndRecordState]);

  const handleNavigateToStarredImages = useCallback((starType: StarType, starLabel: string) => {
    setCurrentView('studio');
    const newFilter: ActiveFilter = {
      type: 'star',
      value: starType, 
      displayName: `Favoritas: ${starLabel}`,
    };
    navigateAndRecordState("filter_view_star", false, newFilter, null);
    setEditMode(EditMode.QuickEdit);
  }, [navigateAndRecordState]);

  const handleNavigateToImageLocation = useCallback((path: string) => {
    if (currentView !== 'studio') setCurrentView('studio');
    const isGallery = true; 
    navigateAndRecordState(path, isGallery, null, null); 
    setEditMode(EditMode.QuickEdit); 
    setSelectedS3File(null); 
  }, [currentView, navigateAndRecordState]);

  const handleImageDirtiedByToolbarAction = (actionLabel: string) => {
    console.log(`App.tsx: handleImageDirtiedByToolbarAction called with: ${actionLabel}`);
    if (!currentDisplayedImage) {
      console.warn("App.tsx: Cannot dirty image: no current image displayed.");
      return;
    }
    console.log("App.tsx: Current showSaveOptionsButton before set:", showSaveOptionsButton);
    
    setCurrentDisplayedImage(prev => {
      if (!prev) return null;
      console.log("App.tsx: Updating currentDisplayedImage to mark as unsaved.");
      return {
        ...prev,
        isSaved: false,
        isExported: false,
        savedToCustomFolderPath: undefined,
        s3Prefix: undefined, 
      };
    });

    setShowSaveOptionsButton(true);
    console.log("App.tsx: setShowSaveOptionsButton(true) has been called.");
    
    setPendingNotificationMessage({ type: 'user', message: `Acción '${actionLabel}' aplicada (simulado). La imagen está lista para guardarse de nuevo.` });
  };

  const handleClearFilter = useCallback(() => {
    navigateAndRecordState("", false, null, null);
    setEditMode(EditMode.Generate);
    setShowSaveOptionsButton(false); 
  }, [navigateAndRecordState]);

  const handleSetBrowsePath = useCallback((newPath: string) => {
    let newIsUserGalleryActive = isUserGalleryActive;
    if (newPath.startsWith(GENERATED_IMAGES_FASE_PATH) || newPath.startsWith(EXPORTED_IMAGES_FASE_PATH)) {
        newIsUserGalleryActive = true;
    } else if (newPath === "") {
        newIsUserGalleryActive = false;
    }
    else if (newPath.includes('/')) {
    } else {
        newIsUserGalleryActive = false;
    }
    navigateAndRecordState(newPath, newIsUserGalleryActive, null, null);
    setShowSaveOptionsButton(false); 
  }, [isUserGalleryActive, navigateAndRecordState]);

  const restoreHistoryState = (state: NavigationState) => {
    setBrowseToPath(state.browseToPath);
    setIsUserGalleryActive(state.isUserGalleryActive);
    setActiveFilter(state.activeFilter);
    setSelectedS3File(null);
    setCurrentDisplayedImage(null);
    setAnalysisResult(null);
    setError(null);
    setAutoSaveMessage(null);
    setUserSaveMessage(null);
    setPendingNotificationMessage(null);
    setShowSaveOptionsButton(false); 
    if (state.selectedS3FileId) {
        console.log("Restoring history with selected file ID:", state.selectedS3FileId, "- full restoration of selected file not yet implemented in history.");
    }
  };

  const goBack = () => {
    if (currentHistoryIndex > 0) {
      const newIndex = currentHistoryIndex - 1;
      setCurrentHistoryIndex(newIndex);
      restoreHistoryState(navigationHistory[newIndex]);
    }
  };

  const goForward = () => {
    if (currentHistoryIndex < navigationHistory.length - 1) {
      const newIndex = currentHistoryIndex + 1;
      setCurrentHistoryIndex(newIndex);
      restoreHistoryState(navigationHistory[newIndex]);
    }
  };

  const handleWindowMouseMove = useCallback((e: MouseEvent) => {
    if (isResizingHorizontal && horizontalResizeContainerRef.current) {
      const containerRect = horizontalResizeContainerRef.current.getBoundingClientRect();
      const newWidthPx = e.clientX - containerRect.left;
      let newWidthPercent = (newWidthPx / containerRect.width) * 100;
      newWidthPercent = Math.max(20, Math.min(50, newWidthPercent));
      setLeftPanelWidthPercent(newWidthPercent);
    }
    if (isResizingVertical && verticalResizeContainerRef.current) {
      const containerRect = verticalResizeContainerRef.current.getBoundingClientRect();
      const newHeightPx = e.clientY - containerRect.top;
      let newHeightPercent = (newHeightPx / containerRect.height) * 100;
      newHeightPercent = Math.max(30, Math.min(70, newHeightPercent));
      setTopRightPanelHeightPercent(newHeightPercent);
    }
  }, [isResizingHorizontal, isResizingVertical]);

  const handleWindowMouseUp = useCallback(() => {
    setIsResizingHorizontal(false);
    setIsResizingVertical(false);
  }, []);

  useEffect(() => {
    if (isResizingHorizontal || isResizingVertical) {
      document.addEventListener('mousemove', handleWindowMouseMove);
      document.addEventListener('mouseup', handleWindowMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleWindowMouseMove);
      document.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [isResizingHorizontal, isResizingVertical, handleWindowMouseMove, handleWindowMouseUp]);

  const startHorizontalResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingHorizontal(true);
  }, []);

  const startVerticalResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingVertical(true);
  }, []);

  const clearNotificationBannerCountdown = useCallback(() => {
    // LUMINA_DEBUG: console.log('LUMINA_DEBUG: clearNotificationBannerCountdown called.');
    if (notificationBannerIntervalRef.current) {
      clearInterval(notificationBannerIntervalRef.current);
      notificationBannerIntervalRef.current = null;
    }
    setNotificationBannerCountdown(null);
    setNotificationBannerAnimateXClick(false);
  }, []);

  useEffect(() => {
    // LUMINA_DEBUG: console.log('LUMINA_DEBUG: Banner message check (E):', { error, autoSaveMessage, userSaveMessage, isSaveModalCurrentlyOpen });
    if ((error || autoSaveMessage || userSaveMessage) && !isSaveModalCurrentlyOpen) {
      // LUMINA_DEBUG: console.log('LUMINA_DEBUG: Banner message detected and modal is closed. Starting countdown.');
      if (notificationBannerIntervalRef.current) {
        clearInterval(notificationBannerIntervalRef.current);
      }
      setNotificationBannerCountdown(5);
      setNotificationBannerAnimateXClick(false);

      notificationBannerIntervalRef.current = setInterval(() => {
        setNotificationBannerCountdown(prevCountdown => {
          // LUMINA_DEBUG: console.log('LUMINA_DEBUG: Banner tick. Prev:', prevCountdown);
          if (prevCountdown === null) { // Should not happen if started correctly
             if (notificationBannerIntervalRef.current) clearInterval(notificationBannerIntervalRef.current);
             notificationBannerIntervalRef.current = null;
             return null;
          }
          if (prevCountdown <= 1) { 
            // LUMINA_DEBUG: console.log('LUMINA_DEBUG: Banner countdown reaching 0.');
            if (notificationBannerIntervalRef.current) clearInterval(notificationBannerIntervalRef.current);
            notificationBannerIntervalRef.current = null;
            
            setNotificationBannerAnimateXClick(true);
            setTimeout(() => {
              setError(null);
              setAutoSaveMessage(null);
              setUserSaveMessage(null);
              clearNotificationBannerCountdown(); // Use the main clear function
              // LUMINA_DEBUG: console.log('LUMINA_DEBUG: Banner messages and countdown fully cleared after animation.');
            }, 300); 
            return 0; 
          }
          return prevCountdown - 1;
        });
      }, 1000);
    } else if (!(error || autoSaveMessage || userSaveMessage)) {
      // LUMINA_DEBUG: console.log('LUMINA_DEBUG: No messages for banner, ensuring countdown is cleared.');
      clearNotificationBannerCountdown();
    }

    return () => {
      // LUMINA_DEBUG: console.log('LUMINA_DEBUG: Banner useEffect (message change) cleanup.');
      if (notificationBannerIntervalRef.current) {
        clearInterval(notificationBannerIntervalRef.current);
        notificationBannerIntervalRef.current = null;
      }
    };
  }, [error, autoSaveMessage, userSaveMessage, isSaveModalCurrentlyOpen, clearNotificationBannerCountdown]);


  const canGoBack = currentHistoryIndex > 0;
  const canGoForward = currentHistoryIndex < navigationHistory.length - 1;
  const combinedQuickEditActions = [...DEFAULT_QUICK_EDIT_ACTIONS, ...customQuickEditActions];
  const anyLoading = isLoadingDescription || isLoadingOperation || isAutoSavingImage || isSavingToDrafts || isSavingToFinalArtwork || !!isSavingToCustomFolderId;
  const anyUserSaveInProgress = isSavingToDrafts || isSavingToFinalArtwork || !!isSavingToCustomFolderId;

  return (
    <div className="flex flex-col h-screen antialiased text-gray-100 bg-gray-900">
      <header className="bg-gray-800 text-white p-4 shadow-md flex justify-between items-center">
        <div className="flex items-center flex-1">
            <button
              onClick={handleHomeIconClick}
              onMouseEnter={() => setHoveredDebugElement("Header: Home Button")}
              onMouseLeave={() => setHoveredDebugElement(null)}
              className="mr-3 p-1 rounded-full hover:bg-gray-700 transition-colors"
              title="Ir a la página de inicio"
              aria-label="Ir a la página de inicio"
            >
              <HomeIcon className="w-6 h-6 text-indigo-300" />
            </button>
            <button 
              onClick={handleStudioTitleClick} 
              onMouseEnter={() => setHoveredDebugElement("Header: Studio Title")}
              onMouseLeave={() => setHoveredDebugElement(null)}
              className="text-2xl font-semibold transition-colors text-gradient-animated" 
              title="Ir a categorías principales del estudio"
            >
              Estudio de Imágenes con IA
            </button>
            {currentView === 'studio' && (
              <div className="ml-4 flex items-center space-x-2">
                <button
                  onClick={goBack}
                  onMouseEnter={() => setHoveredDebugElement("Header: Navigate Back")}
                  onMouseLeave={() => setHoveredDebugElement(null)}
                  disabled={!canGoBack}
                  title="Atrás"
                  aria-label="Navegar atrás"
                  className="p-1 rounded-full hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowLeftIcon className={`w-7 h-7 ${canGoBack ? 'text-green-400 hover:text-green-300' : 'text-gray-500'}`} />
                </button>
                <button
                  onClick={goForward}
                  onMouseEnter={() => setHoveredDebugElement("Header: Navigate Forward")}
                  onMouseLeave={() => setHoveredDebugElement(null)}
                  disabled={!canGoForward}
                  title="Adelante"
                  aria-label="Navegar adelante"
                  className="p-1 rounded-full hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowRightIcon className={`w-7 h-7 ${canGoForward ? 'text-red-400 hover:text-red-300' : 'text-gray-500'}`} />
                </button>
              </div>
            )}
        </div>
        {(currentView === 'studio' && (isAutoSavingImage || anyUserSaveInProgress)) && (
            <div className="flex-1 text-center text-sm text-sky-300 flex items-center justify-center">
                <LoadingSpinner size="sm" />
                <span className="ml-2">
                  {isAutoSavingImage ? "Autoguardando imagen..." : 
                   isSavingToDrafts ? "Guardando en borradores..." : 
                   isSavingToFinalArtwork ? "Grabando imagen final..." : 
                   isSavingToCustomFolderId ? "Guardando en carpeta..." : "Procesando..."}
                </span>
            </div>
        )}
        <div className="flex-1 flex justify-end items-center">
            <button
              onClick={handleHeaderUserClick}
              onMouseEnter={() => setHoveredDebugElement(`Header: User Profile Button (${userProfile.username})`)}
              onMouseLeave={() => setHoveredDebugElement(null)}
              className="flex items-center text-sm font-medium text-white hover:text-gray-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 rounded px-2 py-1"
              title={`Ver perfil de ${userProfile.username}`}
            >
              <UserIcon className="w-5 h-5 mr-1.5 text-indigo-300" />
              <span>Usuario: </span>
              <span className="text-green-300 ml-1">{userProfile.username}</span>
              <span className="text-xs text-gray-400 ml-1">({currentUserTier})</span>
            </button>
        </div>
      </header>

      {(currentView === 'studio' && (error || autoSaveMessage || userSaveMessage)) && (
        <div
            className={`p-3 text-white flex items-center justify-between ${
                error ? 'bg-red-500' :
                ((autoSaveMessage && autoSaveMessage.toLowerCase().includes("error")) || (userSaveMessage && userSaveMessage.toLowerCase().includes("error"))) ? 'bg-yellow-600' :
                'bg-green-600'
            }`}
        >
          <p className="flex-grow mr-4">{error || autoSaveMessage || userSaveMessage}</p>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {notificationBannerCountdown !== null && (
              <span className={`text-sm font-semibold ${notificationBannerAnimateXClick && notificationBannerCountdown === 0 ? 'opacity-100' : 'opacity-90'}`}>
                {notificationBannerCountdown}
              </span>
            )}
            <button 
              onClick={() => { 
                clearNotificationBannerCountdown(); 
                setError(null); setAutoSaveMessage(null); setUserSaveMessage(null); 
              }} 
              onMouseEnter={() => setHoveredDebugElement("Notification Banner: Close Button")}
              onMouseLeave={() => setHoveredDebugElement(null)}
              className={`p-1 rounded-full transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-gray-800
                ${(notificationBannerCountdown !== null && notificationBannerCountdown > 0) ? 'animate-pulse ring-white' : ''}
                ${notificationBannerAnimateXClick && notificationBannerCountdown === 0 ? 'transform scale-75 brightness-125 bg-black bg-opacity-30 ring-white' : 'hover:bg-black hover:bg-opacity-20 ring-gray-300'}
              `}
              aria-label="Descartar notificación"
              title="Descartar notificación"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {currentView === 'landing' && (
        <LandingPage
          onNavigateToGenerate={() => navigateToStudioSection(EditMode.Generate, false, "")}
          onNavigateToMyCreations={() => navigateToStudioSection(EditMode.QuickEdit, true, GENERATED_IMAGES_FASE_PATH + "/" + userProfile.username + "/")}
          onNavigateToMyExports={() => navigateToStudioSection(EditMode.QuickEdit, true, EXPORTED_IMAGES_FASE_PATH + "/" + userProfile.username + "/")}
          onNavigateToMainCategories={() => navigateToStudioSection(EditMode.QuickEdit, false, "")}
          onNavigateToManageCustomEdits={() => navigateToStudioSection(EditMode.ManageCustomEdits)}
          onNavigateToVideoFromImage={handleNavigateToVideoFromImage}
          onNavigateToVideoWithAudio={handleNavigateToVideoWithAudio}
        />
      )}

      {currentView === 'studio' && (
        <div ref={horizontalResizeContainerRef} className="flex flex-1 overflow-hidden">
          <aside
            style={{ width: `${leftPanelWidthPercent}%`, flexShrink: 0 }}
            className="bg-gray-800 overflow-y-auto shadow-lg"
          >
            <ImageBrowser
              onSelectFile={handleSelectS3File}
              selectedFileId={selectedS3File?.id ?? currentDisplayedImage?.id ?? null} 
              targetPath={browseToPath}
              isUserGalleryActive={isUserGalleryActive}
              username={userProfile.username}
              customFolders={customFolders} 
              activeFilter={activeFilter}
              onClearFilter={handleClearFilter}
              onSetBrowsePath={handleSetBrowsePath}
              onDebugHover={setHoveredDebugElement}
              key={isUserGalleryActive.toString() + '-' + browseToPath + '-' + refreshCounter + '-' + JSON.stringify(activeFilter)}
            />
          </aside>

          <div
            onMouseDown={startHorizontalResize}
            onMouseEnter={() => setHoveredDebugElement("UI: Horizontal Resizer")}
            onMouseLeave={() => setHoveredDebugElement(null)}
            className="w-2 bg-gray-700 hover:bg-indigo-600 cursor-col-resize flex-shrink-0 transition-colors"
            title="Arrastrar para cambiar tamaño"
          />

          <main
            ref={verticalResizeContainerRef}
            style={{ width: `${100 - leftPanelWidthPercent}%`, flexShrink: 0 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div
              style={{ height: `${topRightPanelHeightPercent}%`, flexShrink: 0 }}
              className="bg-gray-800 rounded-lg shadow-xl p-2 md:p-4 flex items-center justify-center overflow-auto"
            >
              {anyLoading && !currentDisplayedImage ? <LoadingSpinner text={isLoadingDescription ? "Cargando descripción..." : "Procesando..."} /> :
               <ImageViewer
                  image={currentDisplayedImage}
                  isProcessing={isLoadingOperation || isAutoSavingImage}
                  isSavingToDrafts={isSavingToDrafts}
                  isSavingToFinalArtwork={isSavingToFinalArtwork}
                  isSavingToCustomFolderId={isSavingToCustomFolderId}
                  onConfirmSaveToDrafts={handleSaveToDrafts}
                  onConfirmSaveToFinalArtwork={handleSaveToFinalArtwork}
                  onConfirmSaveToCustomFolder={handleSaveToCustomFolder}
                  customFolders={customFolders}
                  onAddCustomFolder={handleAddCustomFolder}
                  onApplyFilter={handleApplyImageViewerFilter}
                  onNavigateToStore={handleNavigateToStore}
                  onNavigateToImageLocation={handleNavigateToImageLocation}
                  currentStar={currentDisplayedImage?.star}
                  onSetStar={handleSetImageStar}
                  showSaveOptionsButton={showSaveOptionsButton} 
                  onImageDirtied={handleImageDirtiedByToolbarAction}
                  onDebugHover={setHoveredDebugElement}
                  setIsSaveModalOpen={setIsSaveModalCurrentlyOpen} // Pass setter to ImageViewer
               />}
              {!currentDisplayedImage && !anyLoading && (
                <div className="text-center text-gray-400">
                  <p className="text-xl">
                    {activeFilter ? `Viendo resultados para "${activeFilter.displayName}"` : "Selecciona una imagen o genera una nueva."}
                  </p>
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mx-auto mt-4 text-gray-500">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.158 0a.225.225 0 0 1 .225.225 0 0 1-.225.225.225.225 0 0 1-.225-.225.225.225 0 0 1 .225-.225Z" />
                  </svg>
                   <p className="text-xs mt-2">
                     { (process.env.REACT_APP_WORDPRESS_API_URL) ?
                      `Intentando conectar con WordPress en: ${process.env.REACT_APP_WORDPRESS_API_URL}` :
                      "Usando datos S3 simulados. Configura REACT_APP_WORDPRESS_API_URL para conectar con WordPress."}
                   </p>
                </div>
              )}
            </div>

            <div
                onMouseDown={startVerticalResize}
                onMouseEnter={() => setHoveredDebugElement("UI: Vertical Resizer")}
                onMouseLeave={() => setHoveredDebugElement(null)}
                className="h-2 bg-gray-700 hover:bg-indigo-600 cursor-row-resize flex-shrink-0 transition-colors"
                title="Arrastrar para cambiar tamaño"
            />

            <div
              style={{ height: `${100 - topRightPanelHeightPercent}%`, flexShrink: 0 }}
              className="bg-gray-800 rounded-lg shadow-xl p-4 md:p-6 overflow-auto"
            >
              <EditPanel
                editMode={editMode}
                onSetEditMode={setEditMode}
                generationPrompt={generationPrompt}
                onGenerationPromptChange={setGenerationPrompt}
                onGenerate={handleGenerateImageFromPrompt}
                analysisPrompt={analysisPrompt}
                onAnalysisPromptChange={setAnalysisPrompt}
                onAnalyze={handleAnalyzeImage}
                analysisResult={analysisResult}
                isProcessing={isLoadingOperation || isAutoSavingImage || anyUserSaveInProgress}
                selectedImageName={currentDisplayedImage?.displayName ?? selectedS3File?.displayName ?? selectedS3File?.name ?? null}
                selectedImageParsedDate={currentDisplayedImage?.parsedDate ?? selectedS3File?.parsedDate}
                selectedImageParsedTime={currentDisplayedImage?.parsedTime ?? selectedS3File?.parsedTime}
                selectedImageParsedMachineCode={currentDisplayedImage?.parsedMachineCode ?? selectedS3File?.parsedMachineCode}
                selectedImageDescription={selectedS3File?.descriptionText ?? currentDisplayedImage?.rawDescription ?? null}
                currentImage={currentDisplayedImage}
                quickEditActions={combinedQuickEditActions}
                onApplyQuickEdit={handleApplyQuickEdit}
                customQuickEditActions={customQuickEditActions}
                onAddCustomQuickEdit={handleAddCustomQuickEdit}
                onDebugHover={setHoveredDebugElement}
              />
            </div>
          </main>
        </div>
      )}

      {currentView === 'store' && imageForStore && (
        <StorePage
          imageForStore={imageForStore}
          onBackToStudio={handleBackToStudioFromStore}
        />
      )}

      {currentView === 'profile' && (
        <UserProfilePage
          profile={userProfile}
          currentTier={currentUserTier}
          onTierChange={handleTierChange}
          onNavigateBack={handleBackToStudioFromProfile}
          onNavigateToStarredImages={handleNavigateToStarredImages}
        />
      )}
      <DebugViewIndicator currentView={currentView} hoveredElement={hoveredDebugElement} />
    </div>
  );
};

export default App;
